import { ref, computed, watch } from '@vue/composition-api'
// import { title } from '@core/utils/filter'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import ability from '@/libs/acl/ability'
import axios from '@axios'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  const plateFilter = ref(null)
  const cameraFilter = ref(null)
  const startDatetimeFilter = ref(null)
  const endDatetimeFilter = ref(null)
  const cameraOptions = ref([])

  axios('v1/vaxtor_camera?max=2000')
    .then(response => {
      cameraOptions.value = response.data.data.map(unit => ({ label: unit.name, value: unit.id }))
    })

  const tableColumns = [
    { label: 'lpr_matches.date_of_capture', key: 'takenOn', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'lpr_matches.plate_detected', key: 'plateNumber', sortable: true },
    { label: 'lpr_matches.original_plate', key: 'originalPlateNumber', sortable: true },
    { label: 'lpr_matches.picture_plate', key: 'picture_plate', sortable: true },
    { label: 'lpr_matches.status', key: 'status', sortable: true },
    { label: 'lpr_matches.cameras', key: 'cameraName', sortable: true },
    { label: 'lpr_matches.country', key: 'country', sortable: true },
    { label: 'lpr_matches.direction', key: 'direction', sortable: true },
    { label: 'lpr_matches.speed', key: 'speed', sortable: true },
    { label: 'lpr_matches.zone_id', key: 'zoneId', sortable: true },
    { label: 'lpr_matches.original_city', key: 'originalCountry', sortable: true },
    { label: 'lpr_matches.reliability', key: 'confidence', sortable: true },
    { label: 'lpr_matches.ocr_image_id', key: 'ocrImageId', sortable: true },
    { label: 'lpr_matches.signposted', key: 'signaled', sortable: true },
    { label: 'lpr_matches.digit_height', key: 'avgDigitsHeight', sortable: true },
    { label: 'lpr_matches.multiplate_rate', key: 'multiplateRate', sortable: true },
    { label: 'lpr_matches.processing_time_ocr', key: 'processingTimeOCR', sortable: true },
    { label: 'lpr_matches.kamikaze_alarm', key: 'kamikazeAlarm', sortable: true },
    { label: 'lpr_matches.time_in_zone_alarm', key: 'timeInZoneAlarm', sortable: true },
    { label: 'lpr_matches.alarm_blacklist', key: 'blacklistAlarm', sortable: true },
    { label: 'lpr_matches.alarm_speed', key: 'speedAlarm', sortable: true },
    { label: 'lpr_matches.prowling_alarm', key: 'prowlingAlarm', sortable: true },
    { label: 'lpr_matches.vehicle_model', key: 'vehicleModel', sortable: true },
    { label: 'lpr_matches.vehicle_color', key: 'vehicleColor', sortable: true },
    { label: 'lpr_matches.cross_time', key: 'crosstime', sortable: true },
    { label: 'lpr_matches.has_exit', key: 'hasExit', sortable: true },
    { label: 'lpr_matches.signal_id', key: 'signalId', sortable: true },
    { label: 'lpr_matches.category', key: 'category', sortable: true },
    { label: 'lpr_matches.vehicle_class', key: 'vehicleClass', sortable: true },
    { label: 'lpr_matches.region', key: 'region', sortable: true },
    { label: 'lpr_matches.alarm', key: 'isAlarm', sortable: true },
    { label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
  ]

  if (ability.can('update', 'Lpr') == true) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('lpr_coincidences/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      plate: plateFilter.value,
      camera: cameraFilter.value,
      startDatetime: startDatetimeFilter.value,
      endDatetime: endDatetimeFilter.value,
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage, plateFilter, cameraFilter, startDatetimeFilter, endDatetimeFilter], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      plate: plateFilter.value,
      camera: cameraFilter.value,
      startDatetime: startDatetimeFilter.value,
      endDatetime: endDatetimeFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    plateFilter.value = null;
    cameraFilter.value = null;
    startDatetimeFilter.value = null;
    endDatetimeFilter.value = null;
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,

    plateFilter,
    cameraFilter,
    cameraOptions,
    startDatetimeFilter,
    endDatetimeFilter,

    refetchData,

    buildFilters,
    clearSearchFilters,
    filtersCount,
  }
}
